<template>
  <SelectRoot
    v-model:open="isOpen"
    :model-value="modelValue"
    :disabled="disabled"
    @update:model-value="updateValue"
  >
    <SelectTrigger
      :id="settingId"
      :class="[
        `relative flex w-full bg-input px-2.5 py-1.5 text-left text-sm text-current`,
        `design-system-input gap-2.5 overflow-hidden rounded-lg border outline-2 -outline-offset-1 outline-blue-500 focus-visible:outline`,
      ]"
    >
      <span
        :style="{ fontFamily: modelValue }"
        class="w-4 text-muted-foreground"
        >Aa</span
      >
      <span v-if="!modelValue" class="text-muted-foreground">Inherit</span>
      <SelectValue placeholder="&ZeroWidthSpace;" class="grow" />
      <ChevronDownIcon class="size-5 text-muted-foreground" />
    </SelectTrigger>

    <SelectPortal>
      <Transition
        enter-active-class="transition-opacity duration-100 ease-out"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition duration-75 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <SelectContent
          :collision-padding="10"
          class="z-30 flex flex-col rounded-md bg-gray-900 py-2 text-white shadow-lg dark:border dark:border-border dark:bg-card"
        >
          <SelectScrollUpButton class="-mt-2 grid h-7 place-items-center">
            <ChevronDownIcon
              class="size-4 rotate-180 text-gray-400 dark:text-muted-foreground"
            />
          </SelectScrollUpButton>

          <SelectViewport>
            <SelectGroup>
              <SelectItem
                v-for="{ id, label } in options"
                :key="id"
                :value="id"
                class="group relative mx-2 flex shrink-0 cursor-pointer select-none scroll-m-2 gap-3 self-stretch rounded-md border-none px-3 py-1 text-left text-sm outline-none transition-all duration-75 data-[highlighted]:bg-zinc-700 dark:data-[highlighted]:bg-muted"
              >
                <span
                  :style="{ fontFamily: id }"
                  class="w-4 text-muted-foreground"
                  >Aa</span
                >
                <div class="flex min-w-0 grow flex-col break-words">
                  <SelectItemText
                    class="transition-opacity group-data-[disabled]:opacity-50"
                  >
                    {{ label }}
                  </SelectItemText>
                </div>

                <CheckIcon
                  class="my-0.5 size-4 shrink-0 text-blue-400 opacity-0 transition-opacity duration-75 group-data-[state=checked]:opacity-100"
                />
              </SelectItem>
            </SelectGroup>
          </SelectViewport>

          <SelectScrollDownButton class="-mb-2 grid h-7 place-items-center">
            <ChevronDownIcon
              class="size-4 text-gray-400 dark:text-muted-foreground"
            />
          </SelectScrollDownButton>
        </SelectContent>
      </Transition>
    </SelectPortal>
  </SelectRoot>
</template>

<script setup lang="ts">
import {
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectItemText,
  SelectPortal,
  SelectRoot,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectTrigger,
  SelectValue,
  SelectViewport,
} from "reka-ui";
import { ref, watch } from "vue";

import { useSettingControl } from "@/components/Setting/context";
import CheckIcon from "@/icons/heroicons/check-micro.svg";
import ChevronDownIcon from "@/icons/heroicons/chevron-down-micro.svg";

interface FontOption {
  id: string;
  label: string;
}

const modelValue = defineModel<string>({
  default: "",
});

const isOpen = ref(false);
const fontsLoaded = ref(false);

defineProps<{
  options: readonly FontOption[];
  disabled?: boolean;
}>();

const setting = useSettingControl();
const settingId = setting?.register();

const updateValue = (next: string) => {
  modelValue.value = next;
};

// Lazy-load all theme fonts when dropdown opens
const loadFonts = async () => {
  if (fontsLoaded.value) return;
  fontsLoaded.value = true;

  await Promise.all([
    // Variable fonts
    import("@fontsource-variable/alegreya"),
    import("@fontsource-variable/alegreya/wght-italic.css"),
    import("@fontsource-variable/cabin"),
    import("@fontsource-variable/cabin/wght-italic.css"),
    import("@fontsource-variable/eb-garamond"),
    import("@fontsource-variable/eb-garamond/wght-italic.css"),
    import("@fontsource-variable/figtree"),
    import("@fontsource-variable/figtree/wght-italic.css"),
    import("@fontsource-variable/lora"),
    import("@fontsource-variable/lora/wght-italic.css"),
    import("@fontsource-variable/merriweather"),
    import("@fontsource-variable/merriweather/wght-italic.css"),
    import("@fontsource-variable/montserrat"),
    import("@fontsource-variable/montserrat/wght-italic.css"),
    import("@fontsource-variable/source-serif-4"),
    import("@fontsource-variable/source-serif-4/wght-italic.css"),
    // Regular fonts
    import("@fontsource/aleo/400.css"),
    import("@fontsource/aleo/400-italic.css"),
    import("@fontsource/aleo/700.css"),
    import("@fontsource/aleo/700-italic.css"),
    import("@fontsource/ibm-plex-mono/400.css"),
    import("@fontsource/ibm-plex-mono/400-italic.css"),
    import("@fontsource/ibm-plex-mono/500.css"),
    import("@fontsource/ibm-plex-mono/600.css"),
    import("@fontsource/ibm-plex-mono/700.css"),
    import("@fontsource/ibm-plex-mono/700-italic.css"),
    import("@fontsource/lato/400.css"),
    import("@fontsource/lato/400-italic.css"),
    import("@fontsource/lato/700.css"),
    import("@fontsource/lato/700-italic.css"),
  ]);
};

watch(isOpen, (open) => {
  if (open) {
    loadFonts();
  }
});
</script>
